import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@supabase/supabase-js';
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { zugangsurteil } from '../lib/zugang';
import type { ModulePermissionRow, Role, SharedUser } from '../types';

// ─────────────────────────────────────────────────────────────── Anmelde-Last
// Der Anmelde-Vorgang (Profil, Rolle, Rechte, Zugang — vier Abfragen) lief
// deutlich öfter als nötig; im Callcenter-Protokoll vom 17.08. zwei- bis
// fünfmal pro Seitenaufruf, mit jedem Neustart mehr. Zwei Gründe:
//
//   1. `initialize()` hat `hydrate()` selbst aufgerufen UND danach
//      `onAuthStateChange` registriert. Der Anmelde-Client ruft jeden frisch
//      registrierten Zuhörer sofort mit der bestehenden Sitzung auf
//      (`INITIAL_SESSION`) — der Vorgang lief also grundsätzlich doppelt.
//   2. Das Abo von `onAuthStateChange` wurde weggeworfen und `initialize()`
//      war nicht gegen einen zweiten Aufruf geschützt. Jeder zusätzliche
//      Zuhörer wiederholte bei jedem Anmelde-Ereignis (Token-Erneuerung,
//      Tab-Wechsel, zweiter Tab) den kompletten Vorgang.
//
// Diese drei Modul-Variablen halten den Vorgang jetzt bei genau einem Durchlauf
// pro Person und Sitzung. Sie liegen bewusst außerhalb des Stores: der Store
// wird über `persist` aus dem Browser-Speicher wiederhergestellt, dieser
// Zustand darf das ausdrücklich NICHT (er beschreibt nur, was in DIESEM
// Ladevorgang schon passiert ist).
let initLauf: Promise<void> | null = null;
let authAbo: { unsubscribe: () => void } | null = null;
let geladenFuer: string | null = null;

interface ModulePermission {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

interface AuthState {
  user: User | null;
  profile: SharedUser | null;
  role: Role | null;
  // Admin-Vorschau: „Ansicht als andere Rolle" — ändert nur, was in der
  // Oberfläche sichtbar ist (Menü, Kundenakte, /admin /team /insights).
  // Ändert NICHT die echten Rechte — Datenbank-Zugriffe laufen weiterhin mit
  // der echten Admin-Berechtigung. Nur wirksam, wenn `role` tatsächlich
  // 'admin' ist (siehe effectiveRoleOf) und nicht dauerhaft gespeichert
  // (kein `persist`-Eintrag) — nach einem Reload ist immer die echte Rolle aktiv.
  viewAsRole: Role | null;
  setViewAsRole: (r: Role | null) => void;
  permissions: Record<string, ModulePermission>;
  isInitialized: boolean;
  accessDeniedReason: string | null;
  initialize: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
  canView: (module: string) => boolean;
}

// Liefert die Rolle, nach der sich Navigation/Sichtbarkeit richten soll:
// die echte Rolle, außer ein echter Admin hat gerade eine Vorschau-Rolle
// aktiv gesetzt.
export function effectiveRoleOf(s: { role: Role | null; viewAsRole: Role | null }): Role | null {
  return s.role === 'admin' && s.viewAsRole ? s.viewAsRole : s.role;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      role: null,
      viewAsRole: null,
      setViewAsRole: (r) => set({ viewAsRole: r }),
      permissions: {},
      isInitialized: false,
      accessDeniedReason: null,

      // Mehrfach aufrufbar, läuft aber nur einmal wirklich los: alle Aufrufer
      // hängen sich an denselben Durchlauf. Vorher konnte ein zweiter Aufruf
      // (React-Neuaufbau, zweiter Mount) einen kompletten zweiten
      // Anmelde-Vorgang samt zusätzlichem Zuhörer auslösen.
      initialize: () => {
        initLauf ??= starteAnmeldung(set);
        return initLauf;
      },

      loginWithGoogle: async () => {
        if (!isSupabaseAvailable) throw new Error('Supabase nicht konfiguriert');
        const redirectTo = `${window.location.origin}/auth/callback`;
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo },
        });
        if (error) throw error;
      },

      logout: async () => {
        await supabase.auth.signOut();
        geladenFuer = null;
        set({ user: null, profile: null, role: null, viewAsRole: null, permissions: {} });
      },

      hasRole: (...roles) => {
        const effective = effectiveRoleOf(get());
        return !!effective && roles.includes(effective);
      },
      canView: (module) => !!get().permissions[module]?.canView,
    }),
    {
      name: 'cc-auth',
      // viewAsRole bewusst NICHT persistiert — jeder frische Ladevorgang
      // startet mit der echten Rolle.
      partialize: (s) => ({ profile: s.profile, role: s.role, permissions: s.permissions }),
    }
  )
);

// Einmaliger Anmelde-Vorgang plus Registrierung des Zuhörers. Getrennt von
// `initialize`, damit der Store nur noch die Sperre drumherum hält.
async function starteAnmeldung(
  set: (partial: Partial<AuthState>) => void,
): Promise<void> {
  if (!isSupabaseAvailable) {
    set({ isInitialized: true });
    return;
  }

  const { data } = await supabase.auth.getSession();
  if (!data.session) {
    set({ user: null, profile: null, role: null, viewAsRole: null, permissions: {}, accessDeniedReason: null, isInitialized: true });
  } else {
    // Bewusst VOR `isInitialized: true`: sonst sieht die Oberfläche einen
    // Moment lang „fertig, aber niemand angemeldet" und schickt auf den Login.
    await hydrate(data.session.user, set);
    set({ isInitialized: true });
  }

  if (authAbo) return;
  const { data: abo } = supabase.auth.onAuthStateChange((event, session) => {
    // Die Sitzung von gerade haben wir oben schon selbst geladen — der Client
    // ruft jeden frisch registrierten Zuhörer sofort damit auf.
    if (event === 'INITIAL_SESSION') return;

    if (!session?.user) {
      // Nur ein echtes Abmelden räumt auf. Ein `TOKEN_REFRESHED` ohne Sitzung
      // ist ein fehlgeschlagener Erneuerungsversuch und kein Abmelden — daraus
      // die Person auszuloggen war Teil des Problems.
      if (event === 'SIGNED_OUT') {
        geladenFuer = null;
        set({ user: null, profile: null, role: null, viewAsRole: null, permissions: {}, accessDeniedReason: null });
      }
      return;
    }

    // Dieselbe Person ist schon geladen: nur den Anmelde-Stand auffrischen,
    // nicht Profil/Rolle/Rechte erneut abfragen. Eine Token-Erneuerung, ein
    // Tab-Wechsel oder ein zweiter Tab ändert nichts an der Person — vorher
    // stieß jedes dieser Ereignisse vier Abfragen an.
    if (geladenFuer === session.user.id) {
      set({ user: session.user });
      return;
    }
    void hydrate(session.user, set);
  });
  authAbo = abo.subscription;
}

async function hydrate(
  user: User,
  set: (partial: Partial<AuthState>) => void
): Promise<void> {
  set({ user, accessDeniedReason: null });

  const crmSb = supabase.schema('crm');

  const [profileRes, roleRes, permsRes, accessRes] = await Promise.all([
    crmSb.rpc('my_profile'),
    crmSb.rpc('my_crm_role'),
    crmSb.rpc('get_my_module_permissions'),
    supabase.rpc('has_app_access'),
  ]);

  const urteil = zugangsurteil(accessRes);
  if (urteil === 'verweigert') {
    // Die Anmeldung hat geklappt (Google + shared_users), aber es gibt keinen
    // Callcenter-Zugang. Abmelden und klar sagen, woran es liegt.
    geladenFuer = null;
    await supabase.auth.signOut();
    set({
      user: null, profile: null, role: null, permissions: {},
      accessDeniedReason: 'Du hast (noch) keinen Zugriff auf das Callcenter. Bitte wende dich an einen Admin.',
    });
    return;
  }
  // 'keine-antwort': Sitzung behalten. Siehe lib/zugang.ts — ein Aussetzer der
  // Leitung darf niemanden mitten im Buchen auf den Login werfen.

  // Jede der drei Abfragen wird nur übernommen, wenn sie auch geantwortet hat.
  // Sonst hätte ein einzelner Aussetzer Rolle und Rechte auf leer gesetzt und
  // die halbe Navigation verschwinden lassen; der gespeicherte Stand aus dem
  // letzten Ladevorgang ist in dem Fall die bessere Auskunft.
  const naechste: Partial<AuthState> = { accessDeniedReason: null };
  if (!profileRes.error) {
    naechste.profile = (profileRes.data as SharedUser[] | null)?.[0] ?? null;
  }
  if (!roleRes.error) {
    naechste.role = (roleRes.data as Role | null) ?? null;
  }
  if (!permsRes.error) {
    const permissions: Record<string, ModulePermission> = {};
    (permsRes.data as ModulePermissionRow[] | null)?.forEach((p) => {
      permissions[p.module_key] = {
        canView: !!p.can_view,
        canEdit: !!p.can_edit,
        canDelete: !!p.can_delete,
      };
    });
    naechste.permissions = permissions;
  }

  // Erst merken, wenn der Durchlauf durch ist — ein abgebrochener Versuch soll
  // beim nächsten Ereignis erneut geladen werden.
  if (urteil === 'zugelassen') geladenFuer = user.id;
  set(naechste);
}
